import React, { useState, useEffect, useRef } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { Calendar } from 'primereact/calendar';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { Message } from 'primereact/message';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { classNames } from 'primereact/utils';
import tenantAdminCreditService from '../../services/tenantAdminCreditService';

const TenantAdminCreditManagement = () => {
    // State management
    const [wallets, setWallets] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);
    const [first, setFirst] = useState(0);
    const [rows, setRows] = useState(10);

    // Sorting states
    const [sortField, setSortField] = useState('name');
    const [sortOrder, setSortOrder] = useState(1);

    // Filter states
    const [companies, setCompanies] = useState([]);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [loadingCompanies, setLoadingCompanies] = useState(false);
    const [globalFilter, setGlobalFilter] = useState('');
    const [error, setError] = useState(null);

    // Metrics state
    const [metrics, setMetrics] = useState({
        thisMonth: 0,
        thisQuarter: 0,
        halfYear: 0,
        thisYear: 0
    });

    // Admin balance state
    const [adminBalance, setAdminBalance] = useState(0);

    // Transfer/Reclaim dialog states
    const [transferDialogVisible, setTransferDialogVisible] = useState(false);
    const [reclaimDialogVisible, setReclaimDialogVisible] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [transferCredits, setTransferCredits] = useState(null);
    const [reclaimCredits, setReclaimCredits] = useState(null);
    const [transferReason, setTransferReason] = useState('');
    const [reclaimReason, setReclaimReason] = useState('');
    const [transferNote, setTransferNote] = useState('');
    const [reclaimNote, setReclaimNote] = useState('');
    const [transferring, setTransferring] = useState(false);
    const [reclaiming, setReclaiming] = useState(false);
    const [transferSubmitted, setTransferSubmitted] = useState(false);
    const [reclaimSubmitted, setReclaimSubmitted] = useState(false);

    // Transaction history dialog states
    const [transactionsDialog, setTransactionsDialog] = useState(false);
    const [transactionsUser, setTransactionsUser] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [transactionsLoading, setTransactionsLoading] = useState(false);
    const [transactionsFirst, setTransactionsFirst] = useState(0);
    const [transactionsRows, setTransactionsRows] = useState(10);
    const [transactionsTotalRecords, setTransactionsTotalRecords] = useState(0);
    // Transaction filters as primitives (P14 hygiene — avoid object in deps array)
    const [txnTypeFilter, setTxnTypeFilter] = useState(null);
    const [txnDateFrom, setTxnDateFrom] = useState(null);
    const [txnDateTo, setTxnDateTo] = useState(null);

    const toast = useRef(null);

    // Load everything on initial mount (once)
    useEffect(() => {
        loadCompanies();
        loadWallets();
        loadMetrics();
    }, []);

    // Reload wallets when pagination or sorting changes
    useEffect(() => {
        loadWallets();
    }, [first, rows, sortField, sortOrder]);

    // Load user transactions — deps are primitives for P14 hygiene
    useEffect(() => {
        if (transactionsDialog && transactionsUser) {
            loadUserTransactions();
        }
    }, [transactionsDialog, transactionsUser, transactionsFirst, transactionsRows, txnTypeFilter, txnDateFrom, txnDateTo]);

    const loadCompanies = async () => {
        setLoadingCompanies(true);
        try {
            const response = await tenantAdminCreditService.getCompaniesForFilter();
            if (response.success) {
                setCompanies(response.items || []);
            }
        } catch (error) {
            console.error('Error loading companies:', error?.message || 'Unknown error');
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to load companies',
                life: 5000
            });
        } finally {
            setLoadingCompanies(false);
        }
    };

    const loadMetrics = async () => {
        try {
            const response = await tenantAdminCreditService.getCreditTransferMetrics();
            if (response.success) {
                setMetrics({
                    thisMonth: response.data.this_month || 0,
                    thisQuarter: response.data.this_quarter || 0,
                    halfYear: response.data.half_year || 0,
                    thisYear: response.data.this_year || 0
                });
            }
        } catch (error) {
            console.error('Error loading metrics:', error?.message || 'Unknown error');
        }
    };

    const loadWallets = async () => {
        setLoading(true);
        setError(null);

        try {
            const page = Math.floor(first / rows) + 1;
            const params = {
                page: page,
                limit: rows,
                sort_field: sortField,
                sort_order: sortOrder === 1 ? 'asc' : 'desc',
                company_ID: selectedCompany || undefined,
                search: globalFilter || undefined
            };

            Object.keys(params).forEach(key => {
                if (params[key] === null || params[key] === undefined) {
                    delete params[key];
                }
            });

            const response = await tenantAdminCreditService.getUsersWallets(params);

            if (response.data) {
                setWallets(response.data);
                setTotalRecords(response.pagination?.total || 0);
                setAdminBalance(response.admin_balance || 0);
            } else {
                setWallets([]);
                setTotalRecords(0);
            }
        } catch (error) {
            console.error('Error loading wallets:', error?.message || 'Unknown error');
            setError('Failed to load wallets. Please try again.');
            setWallets([]);
            setTotalRecords(0);
        } finally {
            setLoading(false);
        }
    };

    const loadUserTransactions = async () => {
        if (!transactionsUser) return;

        setTransactionsLoading(true);
        try {
            const page = Math.floor(transactionsFirst / transactionsRows) + 1;
            const formatDate = (date) => {
                if (!date) return null;
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
            };

            const params = {
                page: page,
                limit: transactionsRows,
                type: txnTypeFilter,
                date_from: formatDate(txnDateFrom),
                date_to: formatDate(txnDateTo)
            };

            const response = await tenantAdminCreditService.getUserTransactions(transactionsUser.id, params);

            if (response.success) {
                setTransactions(response.data || []);
                setTransactionsTotalRecords(response.pagination?.total || 0);
            }
        } catch (error) {
            console.error('Error loading user transactions:', error?.message || 'Unknown error');
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to load user transactions',
                life: 5000
            });
        } finally {
            setTransactionsLoading(false);
        }
    };

    const onTransactionsPage = (event) => {
        setTransactionsFirst(event.first);
        setTransactionsRows(event.rows);
    };

    const handleFilterChange = () => {
        setFirst(0);
        loadWallets();
    };

    const clearFilters = () => {
        setGlobalFilter('');
        setSelectedCompany(null);
        setFirst(0);
        setTimeout(() => loadWallets(), 0);
    };

    // Transfer dialog
    const openTransferDialog = (user) => {
        setSelectedUser(user);
        setTransferCredits(null);
        setTransferReason('Tenant admin credit allocation');
        setTransferNote('');
        setTransferSubmitted(false);
        setTransferDialogVisible(true);
    };

    const closeTransferDialog = () => {
        setTransferDialogVisible(false);
        setSelectedUser(null);
        setTransferCredits(null);
        setTransferReason('');
        setTransferNote('');
        setTransferSubmitted(false);
    };

    const handleTransfer = async () => {
        setTransferSubmitted(true);
        if (!transferCredits || transferCredits <= 0) {
            toast.current?.show({
                severity: 'warn',
                summary: 'Warning',
                detail: 'Please enter valid credits amount',
                life: 3000
            });
            return;
        }

        setTransferring(true);
        try {
            const response = await tenantAdminCreditService.transferCredits(
                selectedUser.id,
                transferCredits,
                transferReason,
                transferNote
            );

            if (response.success) {
                toast.current?.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: response.message || 'Credits transferred successfully',
                    life: 5000
                });
                closeTransferDialog();
                loadWallets();
                loadMetrics();
            } else {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: response.message || 'Failed to transfer credits',
                    life: 5000
                });
            }
        } catch (error) {
            console.error('Error transferring credits:', error?.message || 'Unknown error');
            const errorMessage = error.response?.data?.message || 'Failed to transfer credits';
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: errorMessage,
                life: 5000
            });
        } finally {
            setTransferring(false);
        }
    };

    // Reclaim dialog
    const openReclaimDialog = (user) => {
        setSelectedUser(user);
        setReclaimCredits(null);
        setReclaimReason('');
        setReclaimNote('');
        setReclaimSubmitted(false);
        setReclaimDialogVisible(true);
    };

    const closeReclaimDialog = () => {
        setReclaimDialogVisible(false);
        setSelectedUser(null);
        setReclaimCredits(null);
        setReclaimReason('');
        setReclaimNote('');
        setReclaimSubmitted(false);
    };

    const handleReclaim = async () => {
        setReclaimSubmitted(true);
        if (!reclaimCredits || reclaimCredits <= 0) {
            toast.current?.show({
                severity: 'warn',
                summary: 'Warning',
                detail: 'Please enter valid credits amount',
                life: 3000
            });
            return;
        }

        if (!reclaimReason || reclaimReason.trim() === '') {
            toast.current?.show({
                severity: 'warn',
                summary: 'Warning',
                detail: 'Please provide a reason for reclaiming credits',
                life: 3000
            });
            return;
        }

        if (reclaimCredits > selectedUser.credit_balance) {
            toast.current?.show({
                severity: 'warn',
                summary: 'Warning',
                detail: `User only has ${selectedUser.credit_balance} credits available`,
                life: 3000
            });
            return;
        }

        setReclaiming(true);
        try {
            const response = await tenantAdminCreditService.reclaimCredits(
                selectedUser.id,
                reclaimCredits,
                reclaimReason,
                reclaimNote
            );

            if (response.success) {
                toast.current?.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: response.message || 'Credits reclaimed successfully',
                    life: 5000
                });
                closeReclaimDialog();
                loadWallets();
                loadMetrics();
            } else {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: response.message || 'Failed to reclaim credits',
                    life: 5000
                });
            }
        } catch (error) {
            console.error('Error reclaiming credits:', error?.message || 'Unknown error');
            const errorMessage = error.response?.data?.message || 'Failed to reclaim credits';
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: errorMessage,
                life: 5000
            });
        } finally {
            setReclaiming(false);
        }
    };

    // Transaction history dialog
    const openTransactionsDialog = (user) => {
        setTransactionsUser(user);
        setTransactions([]);
        setTransactionsFirst(0);
        setTransactionsRows(10);
        setTransactionsTotalRecords(0);
        setTxnTypeFilter(null);
        setTxnDateFrom(null);
        setTxnDateTo(null);
        setTransactionsDialog(true);
    };

    const closeTransactionsDialog = () => {
        setTransactionsDialog(false);
        setTransactionsUser(null);
    };

    const resetTransactionFilters = () => {
        setTxnTypeFilter(null);
        setTxnDateFrom(null);
        setTxnDateTo(null);
    };

    // KPI card — uniform PrimeReact Card; 2 per row mobile, 5 per row desktop
    const KpiCard = ({ icon, label, value, description }) => (
        <div className="col-6 lg:col">
            <Card className="h-full shadow-1" pt={{ body: { className: 'p-3' } }}>
                <div className="flex justify-content-between align-items-start">
                    <div className="flex flex-column gap-1">
                        <span className="text-600 text-sm font-medium">{label}</span>
                        <span className="text-900 text-xl font-semibold">{value}</span>
                    </div>
                    <div className="flex align-items-center justify-content-center border-round bg-primary-50 p-2">
                        <i className={`pi ${icon} text-xl text-primary`} aria-hidden="true"></i>
                    </div>
                </div>
                <span className="text-500 text-xs block mt-2">{description}</span>
            </Card>
        </div>
    );

    // Column templates
    const nameBodyTemplate = (rowData) => (
        <div className="flex flex-column">
            <span className="font-medium">{rowData.name}</span>
            <small className="text-500">{rowData.email}</small>
            {rowData.company && (
                <small className="text-400 flex align-items-center gap-1">
                    <i className="pi pi-building" aria-hidden="true"></i>
                    {rowData.company.name}
                </small>
            )}
        </div>
    );

    const balanceBodyTemplate = (rowData) => (
        <div className="flex align-items-center gap-1">
            <span className="font-semibold text-primary">{(rowData.credit_balance || 0).toLocaleString()}</span>
            <span className="text-500 text-sm">credits</span>
        </div>
    );

    const actionBodyTemplate = (rowData) => (
        <div className="flex flex-wrap gap-1">
            <Button
                icon="pi pi-arrow-up"
                label="Transfer"
                severity="success"
                outlined
                size="small"
                onClick={() => openTransferDialog(rowData)}
                tooltip="Transfer credits to user"
                tooltipOptions={{ position: 'top' }}
                aria-label={`Transfer credits to ${rowData.name}`}
            />
            <Button
                icon="pi pi-arrow-down"
                label="Reclaim"
                severity="warning"
                outlined
                size="small"
                onClick={() => openReclaimDialog(rowData)}
                tooltip="Reclaim credits from user"
                tooltipOptions={{ position: 'top' }}
                aria-label={`Reclaim credits from ${rowData.name}`}
                disabled={!rowData.credit_balance || rowData.credit_balance === 0}
            />
            <Button
                icon="pi pi-list"
                label="History"
                severity="info"
                outlined
                size="small"
                onClick={() => openTransactionsDialog(rowData)}
                tooltip="View all transactions"
                tooltipOptions={{ position: 'top' }}
                aria-label={`View transaction history for ${rowData.name}`}
            />
        </div>
    );

    // Transaction templates
    const transactionTypeTemplate = (rowData) => {
        const isCredit = rowData.type === 1;
        return (
            <Tag
                value={isCredit ? 'Credit' : 'Debit'}
                severity={isCredit ? 'success' : 'danger'}
                icon={isCredit ? 'pi pi-plus' : 'pi pi-minus'}
            />
        );
    };

    const transactionAmountTemplate = (rowData) => {
        const isCredit = rowData.type === 1;
        return (
            <span className={classNames('font-semibold', isCredit ? 'text-green-600' : 'text-red-600')}>
                {isCredit ? '+' : '-'}{rowData.credits}
            </span>
        );
    };

    const transactionDateTemplate = (rowData) => {
        const date = new Date(rowData.created_at);
        const formatted = new Intl.DateTimeFormat('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        }).format(date);
        const time = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        return (
            <div className="flex flex-column">
                <span className="text-700 text-sm">{formatted}</span>
                <small className="text-500">{time}</small>
            </div>
        );
    };

    const transferTypeLabels = {
        'tenant_admin_transfer': 'Admin Transfer',
        'tenant_admin_reclaim': 'Admin Reclaim',
        'company_admin_transfer': 'Company Transfer',
        'company_admin_reclaim': 'Company Reclaim',
        'api_debit': 'API Usage',
        'purchase': 'Purchase',
        'refund': 'Refund'
    };

    const transferTypeTemplate = (rowData) => {
        if (!rowData.transfer_type) return <span className="text-500">-</span>;
        return transferTypeLabels[rowData.transfer_type] || rowData.transfer_type;
    };

    const partyTemplate = (party) => {
        if (!party) return <span className="text-500">-</span>;
        return (
            <div className="flex flex-column">
                <span className="font-medium">{party.name}</span>
                <small className="text-500">{party.email}</small>
            </div>
        );
    };

    return (
        <>
            <Toast ref={toast} position="top-right" />

            <div className="grid">
                <div className="col-12">
                    {/* Page header */}
                    <div className="mb-4">
                        <h1 className="text-xl font-semibold text-900 m-0 mb-1">Credit Management</h1>
                        <p className="text-600 text-sm m-0">
                            Manage user credits and track credit transfers
                        </p>
                    </div>

                    {/* KPI cards — 5 cards: 2 per row mobile, 5 per row desktop */}
                    <div className="grid mb-3">
                        <KpiCard
                            icon="pi-wallet"
                            label="Your Balance"
                            value={loading ? '…' : adminBalance.toLocaleString()}
                            description="Available credits"
                        />
                        <KpiCard
                            icon="pi-calendar"
                            label="This Month"
                            value={metrics.thisMonth}
                            description="Transfers this month"
                        />
                        <KpiCard
                            icon="pi-chart-bar"
                            label="This Quarter"
                            value={metrics.thisQuarter}
                            description="Current quarter transfers"
                        />
                        <KpiCard
                            icon="pi-chart-line"
                            label="Half Year"
                            value={metrics.halfYear}
                            description="Last 6 months"
                        />
                        <KpiCard
                            icon="pi-file"
                            label="This Year"
                            value={metrics.thisYear}
                            description="Total this year"
                        />
                    </div>

                    {error && (
                        <Message
                            severity="error"
                            text={error}
                            className="w-full mb-3"
                        />
                    )}

                    <Card>
                        {/* Filter bar — 5-slot grid: 2 filters + 2 spacers + action block, ALL in one desktop row */}
                        <div className="surface-ground p-3 border-round mb-3">
                            <div className="grid">
                                <div className="col-6 md:col">
                                    <Dropdown
                                        value={selectedCompany}
                                        options={companies}
                                        onChange={(e) => setSelectedCompany(e.value)}
                                        optionLabel="name"
                                        optionValue="id"
                                        filter
                                        filterBy="name"
                                        placeholder="All Companies"
                                        showClear
                                        className="w-full p-inputtext-sm"
                                        loading={loadingCompanies}
                                        emptyMessage="No companies found"
                                        aria-label="Filter by company"
                                    />
                                </div>
                                <div className="col-6 md:col">
                                    <IconField iconPosition="left" className="w-full">
                                        <InputIcon className="pi pi-search" />
                                        <InputText
                                            value={globalFilter}
                                            onChange={(e) => setGlobalFilter(e.target.value)}
                                            onKeyDown={(e) => e.key === 'Enter' && handleFilterChange()}
                                            placeholder="Name, email, or mobile"
                                            className="w-full p-inputtext-sm"
                                            aria-label="Search users"
                                        />
                                    </IconField>
                                </div>
                                {/* Spacers keep filter slots at exactly 20% width on desktop */}
                                <div className="hidden md:block md:col" aria-hidden="true"></div>
                                <div className="hidden md:block md:col" aria-hidden="true"></div>
                                {/* Action block in slot 5 (20% width on desktop, full row on mobile).
                                    py-1 on mobile to match filter-input height; md:py-2 restores normal padding on desktop */}
                                <div className="col-12 md:col flex gap-2">
                                    <Button
                                        label="Clear"
                                        icon="pi pi-filter-slash"
                                        onClick={clearFilters}
                                        severity="secondary"
                                        outlined
                                        size="small"
                                        className="flex-1 py-1 md:py-2"
                                    />
                                    <Button
                                        label="Apply"
                                        icon="pi pi-check"
                                        onClick={handleFilterChange}
                                        size="small"
                                        className="flex-1 py-1 md:py-2"
                                    />
                                </div>
                            </div>
                        </div>

                        <DataTable
                            value={wallets}
                            lazy
                            loading={loading}
                            paginator
                            first={first}
                            rows={rows}
                            totalRecords={totalRecords}
                            onPage={(e) => {
                                setFirst(e.first);
                                setRows(e.rows);
                            }}
                            sortField={sortField}
                            sortOrder={sortOrder}
                            onSort={(e) => {
                                setSortField(e.sortField);
                                setSortOrder(e.sortOrder);
                                setFirst(0);
                            }}
                            rowsPerPageOptions={[10, 25, 50]}
                            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
                            currentPageReportTemplate="Showing {first}-{last} of {totalRecords}"
                            paginatorClassName="text-sm"
                            pageLinkSize={3}
                            dataKey="id"
                            className="p-datatable-gridlines"
                            responsiveLayout="scroll"
                            scrollable
                            scrollHeight="flex"
                            stripedRows
                            size="small"
                            showGridlines
                            emptyMessage={
                                <div className="flex flex-column align-items-center py-5">
                                    <i className="pi pi-wallet pi-2x text-400 mb-3" />
                                    <span className="text-600">No users found.</span>
                                </div>
                            }
                        >
                            <Column
                                field="name"
                                header="User & Company"
                                body={nameBodyTemplate}
                                sortable
                                frozen
                                alignFrozen="left"
                                style={{ minWidth: '260px' }}
                            />
                            <Column
                                field="credit_balance"
                                header="Credit Balance"
                                body={balanceBodyTemplate}
                                sortable
                                style={{ minWidth: '150px' }}
                            />
                            <Column
                                header="Actions"
                                body={actionBodyTemplate}
                                exportable={false}
                                style={{ minWidth: '320px' }}
                                alignHeader="center"
                                bodyClassName="text-center"
                            />
                        </DataTable>
                    </Card>
                </div>
            </div>

            {/* Transfer Credits Dialog */}
            <Dialog
                visible={transferDialogVisible}
                onHide={() => { if (!transferring) closeTransferDialog(); }}
                header="Transfer Credits"
                modal
                draggable={false}
                closable={!transferring}
                style={{ width: '90vw', maxWidth: '500px' }}
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button
                            label="Cancel"
                            icon="pi pi-times"
                            severity="secondary"
                            outlined
                            size="small"
                            onClick={closeTransferDialog}
                            disabled={transferring}
                        />
                        <Button
                            label="Transfer"
                            icon="pi pi-check"
                            onClick={handleTransfer}
                            loading={transferring}
                            severity="success"
                            size="small"
                        />
                    </div>
                }
            >
                {selectedUser && (
                    <>
                        <div className="mb-3 p-3 surface-100 border-round">
                            <div className="font-medium text-900 mb-1">{selectedUser.name}</div>
                            <div className="text-600 text-sm">{selectedUser.email}</div>
                            <div className="text-500 text-sm mt-2">
                                Current Balance:{' '}
                                <span className="font-semibold text-primary">
                                    {selectedUser.credit_balance} credits
                                </span>
                            </div>
                        </div>

                        <div className="field mb-3">
                            <label htmlFor="transfer-credits" className="block text-sm font-medium text-700 mb-2">
                                Credits Amount <span className="text-red-500">*</span>
                            </label>
                            <InputNumber
                                id="transfer-credits"
                                value={transferCredits}
                                onValueChange={(e) => setTransferCredits(e.value)}
                                min={1}
                                max={999999}
                                inputMode="numeric"
                                className={classNames('w-full p-inputtext-sm', {
                                    'p-invalid': transferSubmitted && (!transferCredits || transferCredits <= 0)
                                })}
                                placeholder="Enter credits amount"
                            />
                            {transferSubmitted && (!transferCredits || transferCredits <= 0) && (
                                <small className="p-error block mt-1" role="alert">
                                    <i className="pi pi-exclamation-circle mr-1" />
                                    Please enter a valid credits amount
                                </small>
                            )}
                        </div>

                        <div className="field mb-3">
                            <label htmlFor="transfer-reason" className="block text-sm font-medium text-700 mb-2">
                                Reason
                            </label>
                            <InputText
                                id="transfer-reason"
                                value={transferReason}
                                onChange={(e) => setTransferReason(e.target.value)}
                                className="w-full p-inputtext-sm"
                                placeholder="e.g., Tenant admin credit allocation"
                            />
                        </div>

                        <div className="field">
                            <label htmlFor="transfer-note" className="block text-sm font-medium text-700 mb-2">
                                Note (optional)
                            </label>
                            <InputTextarea
                                id="transfer-note"
                                value={transferNote}
                                onChange={(e) => setTransferNote(e.target.value)}
                                rows={3}
                                className="w-full p-inputtext-sm"
                                placeholder="Additional notes..."
                            />
                        </div>
                    </>
                )}
            </Dialog>

            {/* Reclaim Credits Dialog */}
            <Dialog
                visible={reclaimDialogVisible}
                onHide={() => { if (!reclaiming) closeReclaimDialog(); }}
                header="Reclaim Credits"
                modal
                draggable={false}
                closable={!reclaiming}
                style={{ width: '90vw', maxWidth: '500px' }}
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button
                            label="Cancel"
                            icon="pi pi-times"
                            severity="secondary"
                            outlined
                            size="small"
                            onClick={closeReclaimDialog}
                            disabled={reclaiming}
                        />
                        <Button
                            label="Reclaim"
                            icon="pi pi-check"
                            onClick={handleReclaim}
                            loading={reclaiming}
                            severity="warning"
                            size="small"
                        />
                    </div>
                }
            >
                {selectedUser && (
                    <>
                        <div className="mb-3 p-3 surface-100 border-round">
                            <div className="font-medium text-900 mb-1">{selectedUser.name}</div>
                            <div className="text-600 text-sm">{selectedUser.email}</div>
                            <div className="text-500 text-sm mt-2">
                                Current Balance:{' '}
                                <span className="font-semibold text-primary">
                                    {selectedUser.credit_balance} credits
                                </span>
                            </div>
                        </div>

                        <Message
                            severity="warn"
                            text="Reclaiming credits will deduct them from the user's balance."
                            className="w-full mb-3"
                        />

                        <div className="field mb-3">
                            <label htmlFor="reclaim-credits" className="block text-sm font-medium text-700 mb-2">
                                Credits Amount <span className="text-red-500">*</span>
                            </label>
                            <InputNumber
                                id="reclaim-credits"
                                value={reclaimCredits}
                                onValueChange={(e) => setReclaimCredits(e.value)}
                                min={1}
                                max={selectedUser.credit_balance}
                                inputMode="numeric"
                                className={classNames('w-full p-inputtext-sm', {
                                    'p-invalid': reclaimSubmitted && (!reclaimCredits || reclaimCredits <= 0)
                                })}
                                placeholder="Enter credits amount"
                            />
                            {reclaimSubmitted && (!reclaimCredits || reclaimCredits <= 0) && (
                                <small className="p-error block mt-1" role="alert">
                                    <i className="pi pi-exclamation-circle mr-1" />
                                    Please enter a valid credits amount
                                </small>
                            )}
                        </div>

                        <div className="field mb-3">
                            <label htmlFor="reclaim-reason" className="block text-sm font-medium text-700 mb-2">
                                Reason <span className="text-red-500">*</span>
                            </label>
                            <InputText
                                id="reclaim-reason"
                                value={reclaimReason}
                                onChange={(e) => setReclaimReason(e.target.value)}
                                className={classNames('w-full p-inputtext-sm', {
                                    'p-invalid': reclaimSubmitted && !reclaimReason.trim()
                                })}
                                placeholder="e.g., Unused credits recovery"
                            />
                            {reclaimSubmitted && !reclaimReason.trim() && (
                                <small className="p-error block mt-1" role="alert">
                                    <i className="pi pi-exclamation-circle mr-1" />
                                    Please provide a reason for reclaiming credits
                                </small>
                            )}
                        </div>

                        <div className="field">
                            <label htmlFor="reclaim-note" className="block text-sm font-medium text-700 mb-2">
                                Note (optional)
                            </label>
                            <InputTextarea
                                id="reclaim-note"
                                value={reclaimNote}
                                onChange={(e) => setReclaimNote(e.target.value)}
                                rows={3}
                                className="w-full p-inputtext-sm"
                                placeholder="Additional notes..."
                            />
                        </div>
                    </>
                )}
            </Dialog>

            {/* Transaction History Dialog */}
            <Dialog
                visible={transactionsDialog}
                onHide={closeTransactionsDialog}
                header="Transaction History"
                modal
                draggable={false}
                maximizable
                style={{ width: '95vw', maxWidth: '1100px' }}
            >
                {transactionsUser && (
                    <>
                        <div className="mb-3 p-3 surface-100 border-round">
                            <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center gap-2">
                                <div>
                                    <div className="font-medium text-900">{transactionsUser.name}</div>
                                    <div className="text-600 text-sm">{transactionsUser.email}</div>
                                </div>
                                <div className="text-right">
                                    <div className="text-500 text-xs">Current Balance</div>
                                    <div className="text-primary font-semibold text-xl">
                                        {(transactionsUser.credit_balance || 0).toLocaleString()} credits
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Inner filter bar — 5-slot grid: 3 filters + 1 spacer + reset, ALL in one desktop row */}
                        <div className="surface-ground p-3 border-round mb-3">
                            <div className="grid">
                                <div className="col-12 md:col">
                                    <Dropdown
                                        value={txnTypeFilter}
                                        options={[
                                            { label: 'All Types', value: null },
                                            { label: 'Credits', value: 1 },
                                            { label: 'Debits', value: 2 }
                                        ]}
                                        onChange={(e) => setTxnTypeFilter(e.value)}
                                        placeholder="All Types"
                                        showClear
                                        className="w-full p-inputtext-sm"
                                        aria-label="Filter by transaction type"
                                    />
                                </div>
                                <div className="col-6 md:col">
                                    <Calendar
                                        value={txnDateFrom}
                                        onChange={(e) => setTxnDateFrom(e.value)}
                                        dateFormat="dd/mm/yy"
                                        placeholder="From Date"
                                        showIcon
                                        iconDisplay="input"
                                        className="w-full p-inputtext-sm"
                                        maxDate={txnDateTo || new Date()}
                                        aria-label="Filter from date"
                                    />
                                </div>
                                <div className="col-6 md:col">
                                    <Calendar
                                        value={txnDateTo}
                                        onChange={(e) => setTxnDateTo(e.value)}
                                        dateFormat="dd/mm/yy"
                                        placeholder="To Date"
                                        showIcon
                                        iconDisplay="input"
                                        className="w-full p-inputtext-sm"
                                        minDate={txnDateFrom}
                                        maxDate={new Date()}
                                        aria-label="Filter to date"
                                    />
                                </div>
                                {/* Spacer keeps filters at 20% width */}
                                <div className="hidden md:block md:col" aria-hidden="true"></div>
                                {/* Reset in slot 5 (20% width desktop, full row mobile) */}
                                <div className="col-12 md:col">
                                    <Button
                                        label="Reset"
                                        icon="pi pi-filter-slash"
                                        severity="secondary"
                                        outlined
                                        size="small"
                                        onClick={resetTransactionFilters}
                                        className="w-full py-1 md:py-2"
                                    />
                                </div>
                            </div>
                        </div>

                        <DataTable
                            value={transactions}
                            lazy
                            paginator
                            first={transactionsFirst}
                            rows={transactionsRows}
                            totalRecords={transactionsTotalRecords}
                            onPage={onTransactionsPage}
                            rowsPerPageOptions={[10, 25, 50]}
                            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
                            currentPageReportTemplate="Showing {first}-{last} of {totalRecords}"
                            paginatorClassName="text-sm"
                            pageLinkSize={3}
                            dataKey="id"
                            loading={transactionsLoading}
                            className="p-datatable-gridlines"
                            responsiveLayout="scroll"
                            scrollable
                            scrollHeight="500px"
                            stripedRows
                            size="small"
                            showGridlines
                            emptyMessage={
                                <div className="flex flex-column align-items-center py-5">
                                    <i className="pi pi-inbox pi-2x text-400 mb-3" />
                                    <span className="text-600">No transactions found.</span>
                                </div>
                            }
                        >
                            <Column
                                field="created_at"
                                header="Date & Time"
                                body={transactionDateTemplate}
                                sortable
                                frozen
                                alignFrozen="left"
                                style={{ minWidth: '150px' }}
                            />
                            <Column
                                field="type"
                                header="Type"
                                body={transactionTypeTemplate}
                                style={{ minWidth: '110px' }}
                            />
                            <Column
                                field="credits"
                                header="Amount"
                                body={transactionAmountTemplate}
                                sortable
                                style={{ minWidth: '110px' }}
                            />
                            <Column
                                field="transfer_type"
                                header="Transfer Type"
                                body={transferTypeTemplate}
                                style={{ minWidth: '150px' }}
                            />
                            <Column
                                header="Transferred From"
                                body={(rowData) => partyTemplate(rowData.transferred_from)}
                                style={{ minWidth: '200px' }}
                            />
                            <Column
                                header="Transferred To"
                                body={(rowData) => partyTemplate(rowData.transferred_to)}
                                style={{ minWidth: '200px' }}
                            />
                            <Column
                                header="Initiated By"
                                body={(rowData) => partyTemplate(rowData.initiated_by)}
                                style={{ minWidth: '200px' }}
                            />
                            <Column
                                field="reason"
                                header="Reason"
                                body={(rowData) => rowData.reason || <span className="text-500">-</span>}
                                style={{ minWidth: '200px' }}
                            />
                            <Column
                                field="note"
                                header="Note"
                                body={(rowData) => rowData.note || <span className="text-500">-</span>}
                                style={{ minWidth: '200px' }}
                            />
                        </DataTable>
                    </>
                )}
            </Dialog>
        </>
    );
};

export default TenantAdminCreditManagement;
